"use client";

import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AnimatedButton } from "@/components/marketing/AnimatedButton";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { trackAnalyticsEvent } from "@/lib/analytics/track";

export function ContactForm(): React.ReactElement {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    restaurantName: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  function updateField(field: keyof typeof formData, value: string): void {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          restaurantName: formData.restaurantName.trim(),
          phone: formData.phone.trim() || undefined,
          message: formData.message.trim(),
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        const msg = body.error ?? "Something went wrong. Please try again.";
        throw new Error(msg);
      }

      setStatus("success");
      trackAnalyticsEvent("submit_contact", {});
      setFormData({
        name: "",
        email: "",
        restaurantName: "",
        phone: "",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong."
      );
      trackAnalyticsEvent("contact_error", {
        error_code: err instanceof Error ? err.message : "unknown",
      });
    }
  }

  if (status === "success") {
    return (
      <motion.div
        className="rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="mx-auto mb-3 flex justify-center">
          <PhosphorIcon
            name="paper-plane-tilt"
            className="h-12 w-12 text-primary"
            aria-hidden="true"
          />
        </div>
        <p className="text-lg font-semibold text-foreground">
          Thanks for reaching out!
        </p>
        <p className="mt-1 text-muted-foreground">
          We&apos;ve received your message and will get back to you shortly.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label
            htmlFor="contact-name"
          >
            Name
          </Label>
          <Input
            id="contact-name"
            type="text"
            placeholder="Alex Chen"
            required
            value={formData.name}
            onChange={(e) => updateField("name", e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        <div className="grid gap-2">
          <Label
            htmlFor="contact-email"
          >
            Email
          </Label>
          <Input
            id="contact-email"
            type="email"
            placeholder="alex@restaurant.ca"
            required
            value={formData.email}
            onChange={(e) => updateField("email", e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="contact-restaurant">Restaurant Name</Label>
          <Input
            id="contact-restaurant"
            type="text"
            placeholder="Bubbles Place"
            required
            value={formData.restaurantName}
            onChange={(e) => updateField("restaurantName", e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        <div className="relative grid gap-2">
          <Label htmlFor="contact-phone">
            Phone <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="contact-phone"
            type="tel"
            placeholder="(555) 555-5555"
            value={formData.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contact-message">Message</Label>
        <Textarea
          id="contact-message"
          placeholder="Tell us how you split tips today…"
          required
          rows={5}
          value={formData.message}
          onChange={(e) => updateField("message", e.target.value)}
          disabled={status === "submitting"}
        />
      </div>
      <AnimatePresence>
        {status === "error" && (
          <motion.p
            className="text-sm text-destructive"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatedButton>
        <Button type="submit" size="lg" disabled={status === "submitting"}>
          <span className="flex items-center gap-2">
            {status === "submitting" ? "Sending..." : "Send Message"}
            {status !== "submitting" && (
              <PhosphorIcon
                name="paper-plane-tilt"
                className="h-4 w-4"
                aria-hidden="true"
              />
            )}
          </span>
        </Button>
      </AnimatedButton>
    </form>
  );
}
